'use client'

import { useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { ContactUnitForm, type ContactUnitFormRef } from '@/components/partials'
import { ContentError, FormSkeleton } from '@/components/partials'
import { type CreateContactUnitParams } from '@/types/contact-unit'
import { useContactUnit, useUpdateContactUnit } from '@/hooks'
import { FormModeEnum } from '@/enums'

type PropsType = {
  id: string
}

const Update = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const contactUnitFormRef = useRef<ContactUnitFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const { data: contactUnit, isLoading, error } = useContactUnit(id)
  const { mutate: updateContactUnit, isPending } = useUpdateContactUnit()

  const handleSubmit = (data: CreateContactUnitParams) => {
    updateContactUnit(
      { id, params: data },
      {
        onSuccess: async updatedContactUnit => {
          setIsRedirecting(true)
          queryClient.setQueryData(['contact-unit', id], updatedContactUnit)
          await queryClient.invalidateQueries({ queryKey: ['contact-units'] })
          toast.success(t('updated_successfully'))
          router.push(`/contact-units/${updatedContactUnit.id}`)
        },
        onError: handleError
      }
    )
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (contactUnitFormRef.current) {
        contactUnitFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_updating'))
  }

  const handleCancel = () => {
    router.push(`/contact-units/${id}`)
  }

  if (isLoading || !contactUnit) {
    return <FormSkeleton fieldRows={1} />
  }

  if (error || !contactUnit) {
    return <ContentError error={error} listUrl="/contact-units" />
  }

  return (
    <ContactUnitForm
      ref={contactUnitFormRef}
      mode={FormModeEnum.UPDATE}
      initialData={contactUnit}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isLoading={isPending || isRedirecting}
    />
  )
}

export default Update
